import {
  IcoChevronDown,
  IcoChevronUp,
  IcoDuplicate,
  IcoPlus,
  IcoSettings,
} from '@components/icons';
import type { CSSProperties, ComponentChildren } from 'preact';
import { Dispatch, StateUpdater, useMemo, useRef, useState } from 'preact/hooks';
import { SalesBlockState, SalespageContent, PaddingSize } from 'server/types';
import { BlockWrapper as SharedBlockWrapper } from 'client/cal/page-builder/components/block-wrapper';
import { substateUpdater } from 'client/lib/hooks';
import { PageSelection, SalesBlockDefinition, PageStateProps } from './types';
import { Button } from '@components/buttons';
import { scrollToSelectedSection } from './scrollutil';
import { Color, autoColor, isLight } from 'shared/colors';
import { BulletStyleCSS } from './bullet-styles';
import { SectionSettingsMenu } from './settings-menu';

type ViewProps = {
  state: SalesBlockState;
  definition: SalesBlockDefinition;
};

type EditProps = ViewProps & {
  id: string;
  version?: number;
  definitions: Record<string, SalesBlockDefinition>;
  isSelected: boolean;
  onSelect(): void;
  onAddBlock(): void;
  position: number;
  selection: PageSelection;
  setSelection(selection: PageSelection): void;
  setState: Dispatch<StateUpdater<SalespageContent>>;
  onClone(id: string): void;
  onDelete(id: string): void;
  onMove(id: string, direction: number): void;
} & PageStateProps;

// Use shared BlockWrapper but with editor-specific features
function BlockWrapper(props: {
  paddingt?: PaddingSize;
  paddingb?: PaddingSize;
  bgcolor?: string;
  children: ComponentChildren;
}) {
  return (
    <SharedBlockWrapper
      paddingt={props.paddingt}
      paddingb={props.paddingb}
      bgcolor={props.bgcolor}
      isEditor={true}
    >
      {props.children}
    </SharedBlockWrapper>
  );
}

export function BlockViewer(props: ViewProps) {
  const { state, definition } = props;
  const elId = `block-${state.id}`;
  const bgcolor = state.bgcolor || Color.white;
  const isBgLight = useMemo(() => isLight(bgcolor), [bgcolor]);
  const fgcolor = state.fgcolor || autoColor(bgcolor);
  const style = blockStyle(state, fgcolor);

  return (
    <div
      id={elId}
      key={state.id}
      style={style}
      data-type={definition.type}
      class={`relative ${isBgLight ? '' : 'sales-page-dark'}`}
    >
      <BulletStyleCSS
        blockId={elId}
        bulletStyle={state.bulletStyle}
        bulletColor={state.bulletColor}
      />

      <BlockWrapper
        bgcolor={bgcolor}
        paddingb={props.state.paddingb}
        paddingt={props.state.paddingt}
      >
        <definition.Viewer
          id={elId}
          state={state.state}
          bgcolor={state.bgcolor}
          fgcolor={fgcolor}
          isBgLight={isBgLight}
          bulletStyle={state.bulletStyle}
          bulletColor={state.bulletColor}
        />
      </BlockWrapper>
    </div>
  );
}

function BtnToolbar({
  Ico,
  tooltip,
  onClick,
  hover = 'hover:bg-gray-100 hover:text-gray-800',
}: {
  Ico: typeof IcoSettings;
  onClick?(): void;
  tooltip: string;
  hover?: string;
}) {
  return (
    <Button
      class={`${hover} relative rounded-full w-6 h-6 inline-flex items-center justify-center`}
      data-tooltip={tooltip}
      onClick={onClick}
    >
      <Ico />
    </Button>
  );
}

function Divider() {
  return <span class="w-px h-6 bg-indigo-500 mx-1"></span>;
}

function BlockStickyToolbar({
  state,
  setState,
  definition,
  onAddBlock,
  onClone,
  onDelete,
  onMove,
}: {
  state: SalesBlockState;
  setState: Dispatch<StateUpdater<SalesBlockState>>;
  definition: SalesBlockDefinition;
  onAddBlock(): void;
  onClone(): void;
  onDelete(): void;
  onMove(id: string, direction: number): void;
}) {
  const [showSettings, setShowSettings] = useState(false);
  return (
    <div class="sticky top-0 z-30 mb-4">
      {showSettings && (
        <SectionSettingsMenu
          state={state}
          setState={setState}
          close={() => setShowSettings(false)}
          onDelete={onDelete}
        />
      )}
      <div class="bg-indigo-600 text-white px-4 p-2 flex items-center gap-3">
        <span class="text-sm font-medium capitalize">{definition.name || definition.type}</span>
        <Divider />
        <Button
          class="hover:bg-indigo-700 p-1.5 rounded text-white/90 hover:text-white"
          data-tooltip="Show Section Settings"
          data-tooltip-y="bottom"
          onClick={() => {
            setShowSettings(true);
          }}
        >
          <IcoSettings class="size-4" />
        </Button>
        <Divider />
        <Button
          class="hover:bg-indigo-700 p-1.5 rounded text-white/90 hover:text-white"
          data-tooltip="Add New Section"
          data-tooltip-y="bottom"
          onClick={(e: Event) => {
            e.stopPropagation();
            onAddBlock();
          }}
        >
          <IcoPlus class="size-4" />
        </Button>
        <Button
          class="hover:bg-indigo-700 p-1.5 rounded text-white/90 hover:text-white"
          data-tooltip="Clone Section"
          data-tooltip-y="bottom"
          onClick={(e: Event) => {
            e.stopPropagation();
            onClone();
          }}
        >
          <IcoDuplicate class="size-4" />
        </Button>
        <Divider />
        <Button
          class="hover:bg-indigo-700 p-1.5 rounded text-white/90 hover:text-white"
          data-tooltip="Move Section Up"
          data-tooltip-y="bottom"
          onClick={(e: Event) => {
            e.stopPropagation();
            onMove(state.id, -1);
          }}
        >
          <IcoChevronUp class="size-4" />
        </Button>
        <Button
          class="hover:bg-indigo-700 p-1.5 rounded text-white/90 hover:text-white"
          data-tooltip="Move Section Down"
          data-tooltip-y="bottom"
          onClick={(e: Event) => {
            e.stopPropagation();
            onMove(state.id, 1);
          }}
        >
          <IcoChevronDown class="size-4" />
        </Button>
      </div>
    </div>
  );
}

export function BlockToolbar(props: Omit<EditProps, 'nextSkewed' | 'isSelected' | 'onSelect'>) {
  const { state } = props;

  return (
    <nav
      class="animate-slide-in absolute z-40 flex flex-col gap-4 border border-gray-100 top-1/4 left-8 bg-white text-gray-600 p-1 pt-1.5 shadow-xl rounded-full"
      onMouseEnter={() => {
        // When the mosuse enters the section context toolbar, we want to ensure
        // the section is scrolled into view so that it's clear *what* section
        // the user is about to modify.
        scrollToSelectedSection({ force: false });
      }}
    >
      <BtnToolbar Ico={IcoPlus} onClick={props.onAddBlock} tooltip="Add New Section" />
      <span class="border-t"></span>
      <BtnToolbar
        Ico={IcoDuplicate}
        tooltip="Clone Section"
        hover="hover:bg-red-50 hover:text-red-600"
        onClick={() => props.onClone(state.id)}
      />
      <span class="border-t"></span>
      <BtnToolbar
        Ico={IcoChevronUp}
        tooltip="Move Section Up"
        onClick={() => props.onMove(state.id, -1)}
      />
      <BtnToolbar
        Ico={IcoChevronDown}
        tooltip="Move Section Down"
        onClick={() => props.onMove(state.id, 1)}
      />
    </nav>
  );
}

export function BlockEditor(
  props: EditProps & {
    onBlockStateChange?: any;
  },
) {
  const { definition, isSelected } = props;
  const blockState = props.state;
  const elId = `block-${blockState.id}`;
  const blockRef = useRef<HTMLDivElement>(null);

  const setBlockState = useMemo(
    () =>
      substateUpdater(
        props.setState,
        (s) => s.blocks[blockState.id],
        (s, sub) => ({
          ...s,
          blocks: {
            ...s.blocks,
            [blockState.id]: sub,
          },
        }),
      ),
    [blockState.id],
  );
  const setState = useMemo(
    () =>
      substateUpdater(
        setBlockState,
        (s) => s.state,
        (s, sub) => ({
          ...s,
          state: sub,
        }),
      ),
    [blockState.id],
  );

  const bgcolor = blockState.bgcolor || Color.white;
  const isBgLight = useMemo(() => isLight(bgcolor), [bgcolor]);
  const fgcolor = blockState.fgcolor || autoColor(bgcolor);
  const style = blockStyle(blockState, fgcolor);

  return (
    <div
      ref={blockRef}
      id={elId}
      key={blockState.id}
      onClick={() => {
        !isSelected && props.onSelect();
      }}
      class={`border-4 ring-inset ring-4 transition-all ${
        isSelected
          ? 'sales-page-active-block ring-white border-indigo-600 shadow-xl z-20'
          : 'ring-transparent border-transparent hover:border-indigo-200 hover:ring-white z-0'
      } ${isBgLight ? '' : 'sales-page-dark'} group relative`}
      style={style}
    >
      {isSelected && (
        <BlockStickyToolbar
          state={blockState}
          setState={setBlockState}
          definition={definition}
          onAddBlock={props.onAddBlock}
          onClone={() => props.onClone(blockState.id)}
          onDelete={() => props.onDelete(blockState.id)}
          onMove={props.onMove}
        />
      )}
      <div>
        <BulletStyleCSS
          blockId={elId}
          bulletStyle={blockState.bulletStyle}
          bulletColor={blockState.bulletColor}
        />

        <BlockWrapper
          bgcolor={bgcolor}
          paddingb={props.state.paddingb}
          paddingt={props.state.paddingt}
        >
          <definition.Editor
            id={props.id}
            isBgLight={isBgLight}
            version={props.version}
            state={blockState.state}
            setState={setState}
            isSelected={isSelected}
            bgcolor={blockState.bgcolor}
            fgcolor={fgcolor}
            selection={props.selection}
            setSelection={props.setSelection}
            bulletStyle={blockState.bulletStyle}
            bulletColor={blockState.bulletColor}
            blockState={blockState}
            onBlockStateChange={props.onBlockStateChange}
            pageState={props.pageState}
            setPageState={props.setPageState}
          />
        </BlockWrapper>
      </div>
    </div>
  );
}

export function blockStyle(
  state: SalesBlockState<any>,
  fgcolor: string | undefined,
): CSSProperties {
  return {
    backgroundColor: state.bgcolor || Color.white,
    backgroundImage: state.bgimg && `url(${state.bgimg})`,
    backgroundSize: 'cover',
    color: fgcolor,
  };
}
